/**
 * Celebration palette — read from the design system, never hardcoded.
 *
 * The celebration is a bloom of *light*, so it has to know two things about the
 * surface it is blooming over: which colours the product is using right now
 * (--accent, --mood-5) and whether we are in warm-paper daylight or evening.
 * Both come out of the live computed styles, which means a manual
 * `:root[data-theme]` override, the OS media query and any future token change
 * all flow through here for free — there is no second copy of the palette to
 * drift.
 *
 * No Three.js in this file: the reduced-motion treatment is plain DOM and must
 * not pull the renderer into the graph.
 */

/** Fallbacks mirror the tokens exactly, for the rare case a var reads empty. */
const FALLBACK = {
  light: { accent: [0.243, 0.498, 0.424], warm: [0.831, 0.643, 0.373] },
  dark: { accent: [0.498, 0.753, 0.659], warm: [0.890, 0.702, 0.424] },
};

/**
 * Parse the colour forms getComputedStyle can hand back: #rgb, #rrggbb,
 * rgb()/rgba() (legacy commas or the modern space syntax). Anything exotic —
 * color(), oklch() — returns null and the caller falls back to a token value.
 * Returns linear-ish sRGB components in 0..1.
 */
export function parseColor(value) {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;

  if (raw[0] === "#") {
    const hex = raw.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      const n = [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16));
      return n.some(Number.isNaN) ? null : n.map((c) => c / 255);
    }
    if (hex.length === 6 || hex.length === 8) {
      const n = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
      return n.some(Number.isNaN) ? null : n.map((c) => c / 255);
    }
    return null;
  }

  const match = raw.match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return null;
  const parts = match[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3);
  if (parts.length < 3) return null;
  const n = parts.map((p) =>
    p.endsWith("%") ? (parseFloat(p) / 100) * 255 : parseFloat(p)
  );
  return n.some(Number.isNaN) ? null : n.map((c) => Math.min(1, Math.max(0, c / 255)));
}

/** Perceived lightness, good enough to tell daylight from evening. */
function luminance([r, g, b]) {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Toward white — additive light needs a bright source or it reads as paint. */
export function lighten(rgb, amount) {
  return rgb.map((c) => c + (1 - c) * amount);
}

export function rgba(rgb, alpha) {
  const [r, g, b] = rgb.map((c) => Math.round(Math.min(1, Math.max(0, c)) * 255));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The palette for one celebration.
 *
 * `dark` is derived from --surface rather than from matchMedia so that a
 * data-theme override is honoured without a second code path.
 *
 * Light mode is deliberately quieter: this is additive light, and warm linen is
 * already near the top of the range, so the same intensity that reads as a soft
 * glow at night would read as a white wash at noon.
 */
export function readCelebrationPalette(el) {
  const root = el || (typeof document !== "undefined" ? document.documentElement : null);
  let surface = null;
  let accent = null;
  let warm = null;

  if (root && typeof getComputedStyle === "function") {
    const styles = getComputedStyle(root);
    surface = parseColor(styles.getPropertyValue("--surface"));
    accent = parseColor(styles.getPropertyValue("--accent"));
    warm = parseColor(styles.getPropertyValue("--mood-5"));
  }

  const dark = surface ? luminance(surface) < 0.45 : false;
  const tokens = dark ? FALLBACK.dark : FALLBACK.light;

  const cool = accent || tokens.accent;
  const glow = warm || tokens.warm;

  // A transparent canvas composites as `colour·a + page·(1-a)`, so a layer only
  // reads as *light* when its colour is brighter than what it lands on. In
  // daylight — a scrim over warm linen, around 70% grey — the deep sage of the
  // accent is darker than the page and would show up as soot. Both hues are
  // therefore lifted most of the way to white in light mode: same colour
  // family, used as illumination rather than as pigment.
  return {
    dark,
    cool: dark ? lighten(cool, 0.12) : lighten(cool, 0.78),
    // The warm end of the mood ramp — "daylight", the one genuinely happy
    // colour in the system.
    warm: dark ? lighten(glow, 0.34) : lighten(glow, 0.52),
    // How hard the light pushes. Tuned per theme; see above.
    moteIntensity: dark ? 1 : 0.92,
    bloomIntensity: dark ? 0.34 : 0.3,
  };
}
